/**
 * AI туслах — header дээрх «Туслах» товч + чат самбар (2026-10-06).
 *
 * Ажилтан системийн тухай асууна; туслах гарын авлагаас хариулж, тайлан
 * хүсвэл Excel татах товч өгнө (`GET /report-files/:kind`). Файлыг ЗӨВХӨН
 * эндээс авна — эзэмшигчийн шийдвэр.
 *
 * ⚠️ Харилцан яриа ЗӨВХӨН энэ компонентын санах ойд — сервер, хөтчийн
 *    storage-д хадгалагдахгүй (асуултад худалдан авагчийн мэдээлэл орж
 *    магадгүй). Сүүлийн 10 мессежийг л илгээнэ.
 * ⚠️ Ажилтан аль хуудсан дээр байгааг (`pathname`) илгээнэ — туслах тэр
 *    дэлгэцийн гарын авлагыг түрүүлж уншина.
 * ⚠️ Товч нь хөвөгч БИШ, header-т: баруун доод буланд кассын «Зарах»
 *    товчийг халхалж байв.
 */

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { reportFileName, type ReportKind } from '@boots/domain';
import { ApiError, api, download, type Envelope } from '../lib/api';

interface Status {
  enabled: boolean;
  monthReached: boolean;
  todayLeft: number;
}

interface FileRef {
  kind: ReportKind;
  title: string;
}

interface Answer {
  answer: string;
  reports: FileRef[];
  todayLeft: number;
}

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  reports?: FileRef[];
  error?: boolean;
}

const EXAMPLES = [
  'Буцаалт яаж хийх вэ?',
  'Хувь зээлийн сарын төлөлтийг хаанаас бүртгэх вэ?',
  'Борлуулалтын бүх мэдээллийг Excel-ээр өгөөч',
];

export function AssistantButton() {
  const [open, setOpen] = useState(false);

  // Escape-ээр хаагдана — бусад цонхтой ижил зан
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="AI туслах"
        title="AI туслах — системийн тухай асуух, тайлан авах"
        className="fill-primary flex size-11 items-center justify-center gap-2 rounded-pill bg-accent text-sm
          font-bold text-white transition active:scale-[0.96] sm:size-auto sm:h-10 sm:px-4"
      >
        <Spark />
        <span className="hidden sm:inline">Туслах</span>
      </button>
      {open ? <Chat onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function Chat({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const status = useQuery({
    queryKey: ['assistant-status'],
    queryFn: () => api<Envelope<Status>>('/assistant/status'),
    staleTime: 30_000,
  });
  const st = status.data?.data;

  const ask = useMutation({
    mutationFn: (history: Msg[]) =>
      api<Envelope<Answer>>('/assistant/ask', {
        method: 'POST',
        body: {
          // Алдааны мессеж түүхэнд орохгүй — туслах өөрийн хэлээгүйг «хэлсэн» гэж уншина
          messages: history.filter((m) => !m.error).slice(-10).map(({ role, content }) => ({ role, content })),
          page: pathname,
        },
      }),
    onSuccess: (res) => {
      setMsgs((prev) => [...prev, { role: 'assistant', content: res.data.answer, reports: res.data.reports }]);
      void status.refetch();
    },
    onError: (e) => {
      const text = e instanceof ApiError ? e.message : 'Холболт тасарлаа — дахин оролдоно уу';
      setMsgs((prev) => [...prev, { role: 'assistant', content: text, error: true }]);
    },
  });

  // Шинэ мессеж ирэхэд доош гүйлгэнэ
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs, ask.isPending]);

  useEffect(() => inputRef.current?.focus(), []);

  function send(text: string) {
    const q = text.trim();
    if (!q || ask.isPending) return;
    const next = [...msgs, { role: 'user' as const, content: q }];
    setMsgs(next);
    setDraft('');
    ask.mutate(next);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    send(draft);
  }

  // Enter — илгээх, Shift+Enter — шинэ мөр
  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  }

  const blocked = st ? !st.enabled || st.monthReached || st.todayLeft === 0 : false;
  const notice = !st
    ? null
    : !st.enabled
      ? 'AI туслах тохируулагдаагүй байна — админд хандана уу.'
      : st.monthReached
        ? 'Энэ сарын AI туслахын хязгаар дууслаа — сарын 1-нд сэргэнэ.'
        : st.todayLeft === 0
          ? 'Өнөөдрийн асуултын хязгаар дууслаа — маргааш дахин асууна уу.'
          : null;

  return (
    <section
      role="dialog"
      aria-label="AI туслах"
      // Дүүрэн `bg-panel` — шилэн `.surface` нь агуулгын дээр нэвт харагдана
      className="rise fixed inset-x-2 top-16 bottom-2 z-50 flex flex-col overflow-hidden rounded-card border
        border-hair bg-panel shadow-pop sm:inset-x-auto sm:top-[72px] sm:right-6 sm:bottom-6 sm:w-[400px]"
    >
      <header className="flex items-center gap-3 border-b border-hair px-4 py-3">
        <span aria-hidden className="fill-primary grid size-9 place-items-center rounded-chip bg-accent text-white">
          <Spark />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-h3 font-bold">AI туслах</h2>
          <p className="text-label text-ink-3">
            Системийн тухай асуух, тайлан Excel-ээр авах
            {st?.enabled && !blocked ? <span className="num"> · өнөөдөр {st.todayLeft} асуулт</span> : null}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Хаах"
          className="grid size-9 place-items-center rounded-full text-ink-2 transition hover:bg-well"
        >
          ✕
        </button>
      </header>

      <div ref={listRef} className="scroll-slim flex min-h-48 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
        {msgs.length === 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-body text-ink-2">
              Сайн байна уу! Системийг хэрхэн ашиглах, эсвэл ямар тайлан хэрэгтэйгээ энгийн үгээр асуугаарай.
            </p>
            <div className="flex flex-col items-start gap-2">
              {EXAMPLES.map((q) => (
                <button
                  key={q}
                  type="button"
                  disabled={blocked}
                  onClick={() => send(q)}
                  className="rounded-chip border border-hair bg-input px-3 py-2 text-left text-meta text-ink-2
                    transition hover:border-accent-300 hover:text-ink disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          msgs.map((m, i) => <Bubble key={i} msg={m} />)
        )}
        {ask.isPending ? (
          <div className="flex items-center gap-2 text-meta text-ink-3" role="status">
            <span aria-hidden className="size-2 animate-pulse rounded-full bg-accent" />
            Бодож байна…
          </div>
        ) : null}
      </div>

      {notice ? <p className="border-t border-hair px-4 py-2 text-meta text-alert-ink">{notice}</p> : null}

      <form onSubmit={submit} className="flex items-end gap-2 border-t border-hair px-3 py-3">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          rows={1}
          maxLength={2000}
          disabled={blocked}
          placeholder="Асуултаа бичнэ үү…"
          aria-label="Асуулт"
          className="max-h-32 min-h-11 flex-1 resize-none rounded-field border border-hair bg-input px-3 py-2.5
            text-base text-ink transition placeholder:text-ink-3 focus:border-accent focus:ring-4
            focus:ring-accent-100 focus:outline-none disabled:opacity-50 sm:text-sm"
        />
        <button
          type="submit"
          disabled={blocked || ask.isPending || !draft.trim()}
          aria-label="Илгээх"
          className="fill-primary grid size-11 shrink-0 place-items-center rounded-btn bg-accent text-white
            transition active:scale-[0.94] disabled:cursor-not-allowed disabled:opacity-45"
        >
          ➤
        </button>
      </form>
    </section>
  );
}

function Bubble({ msg }: { msg: Msg }) {
  const mine = msg.role === 'user';
  return (
    <div className={`flex flex-col gap-2 ${mine ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[88%] rounded-well px-3.5 py-2.5 text-body whitespace-pre-wrap ${
          mine
            ? 'bg-accent-100 text-ink'
            : msg.error
              ? 'border border-alert/30 bg-alert-bg text-alert-ink'
              : 'border border-hair bg-well text-ink'
        }`}
      >
        <Rich text={msg.content} />
      </div>
      {msg.reports?.map((r) => <FileButton key={r.kind} file={r} />)}
    </div>
  );
}

/** Excel татах товч — серверийн эрх, салбарын хязгаарыг `report-files` шалгана. */
function FileButton({ file }: { file: FileRef }) {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const [error, setError] = useState('');

  async function get() {
    setState('busy');
    try {
      await download(`/report-files/${file.kind}`, reportFileName(file.kind, new Date()));
      setState('idle');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Татаж чадсангүй');
      setState('error');
    }
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => void get()}
        disabled={state === 'busy'}
        className="inline-flex min-h-11 items-center gap-2 rounded-btn border border-accent-300 bg-accent-100 px-3.5
          py-2 text-meta font-semibold text-accent-700 transition hover:brightness-105 active:scale-[0.97]
          disabled:opacity-60 sm:min-h-0"
      >
        <span aria-hidden>⬇</span>
        {state === 'busy' ? 'Бэлдэж байна…' : `${file.title} (Excel)`}
      </button>
      {state === 'error' ? <span className="text-label text-alert-ink">{error}</span> : null}
    </div>
  );
}

/** `**тод**` л — гарын авлагын товч, цэсний нэрийг тодоор бичдэг. HTML оруулахгүй. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part,
      )}
    </>
  );
}

function Spark() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 1.5l1.6 3.9 3.9 1.6-3.9 1.6L8 12.5 6.4 8.6 2.5 7l3.9-1.6z"
        fill="currentColor"
      />
      <path d="M13 11l.6 1.4 1.4.6-1.4.6L13 15l-.6-1.4L11 13l1.4-.6z" fill="currentColor" opacity="0.8" />
    </svg>
  );
}
